/* Westerly wishlist
   Device-local wishlist stored in localStorage (no account sync).
   Provides: heart toggle buttons ([data-wishlist-toggle]) and the
   <westerly-wishlist-grid> element used on the wishlist page. */

(function () {
  'use strict';

  const STORAGE_KEY = 'westerly:wishlist';
  const MAX_ITEMS = 100;

  const storage = {
    read() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
      } catch (error) {
        return [];
      }
    },
    write(handles) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_ITEMS)));
        return true;
      } catch (error) {
        return false;
      }
    },
    available() {
      try {
        const test = '__westerly_test__';
        window.localStorage.setItem(test, '1');
        window.localStorage.removeItem(test);
        return true;
      } catch (error) {
        return false;
      }
    },
  };

  const api = {
    list: () => storage.read(),
    has: (handle) => storage.read().includes(handle),
    toggle(handle) {
      const items = storage.read();
      const index = items.indexOf(handle);
      if (index === -1) {
        items.unshift(handle);
      } else {
        items.splice(index, 1);
      }
      storage.write(items);
      document.dispatchEvent(
        new CustomEvent('westerly:wishlist:change', { detail: { handle, saved: index === -1, items } })
      );
      return index === -1;
    },
    remove(handle) {
      const items = storage.read().filter((h) => h !== handle);
      storage.write(items);
      document.dispatchEvent(
        new CustomEvent('westerly:wishlist:change', { detail: { handle, saved: false, items } })
      );
    },
  };

  window.WesterlyWishlist = api;

  function labelFor(button, saved) {
    const add = button.dataset.labelAdd || 'Add to wishlist';
    const remove = button.dataset.labelRemove || 'Remove from wishlist';
    return saved ? remove : add;
  }

  function syncButton(button) {
    const handle = button.dataset.wishlistToggle;
    if (!handle) return;
    const saved = api.has(handle);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', labelFor(button, saved));
    const text = button.querySelector('[data-wishlist-label]');
    if (text) text.textContent = labelFor(button, saved);
  }

  function syncAllButtons(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    if (!storage.available()) {
      button.setAttribute('aria-label', button.dataset.labelUnavailable || 'Wishlist unavailable');
      return;
    }
    api.toggle(button.dataset.wishlistToggle);
  });

  document.addEventListener('westerly:wishlist:change', () => syncAllButtons());

  document.addEventListener('shopify:section:load', (event) => syncAllButtons(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncAllButtons());
  } else {
    syncAllButtons();
  }

  /* Watch for cards injected later (filtering, quick add, remote grids). */
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches && node.matches('[data-wishlist-toggle]')) {
          syncButton(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* Wishlist page grid */
  if (!customElements.get('westerly-wishlist-grid')) {
    customElements.define(
      'westerly-wishlist-grid',
      class WesterlyWishlistGrid extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-remote-grid]');
          this.emptyState = this.querySelector('[data-wishlist-empty]');
          this.onChange = this.render.bind(this);
          document.addEventListener('westerly:wishlist:change', this.onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener('westerly:wishlist:change', this.onChange);
        }

        rootUrl() {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          return root.endsWith('/') ? root : `${root}/`;
        }

        async fetchCard(handle) {
          const url = `${this.rootUrl()}products/${encodeURIComponent(handle)}?section_id=product-card-standalone`;
          const response = await fetch(url);
          if (response.status === 404) {
            window.WesterlyWishlist.remove(handle);
            return null;
          }
          if (!response.ok) return null;
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('.w-standalone-card');
          return card && card.firstElementChild ? card : null;
        }

        async render() {
          const handles = window.WesterlyWishlist.list();
          if (!this.grid) return;

          if (!handles.length) {
            this.grid.innerHTML = '';
            if (this.emptyState) this.emptyState.hidden = false;
            return;
          }
          if (this.emptyState) this.emptyState.hidden = true;

          const token = (this.renderToken = Symbol('render'));
          const results = await Promise.all(
            handles.map((handle) => this.fetchCard(handle).catch(() => null))
          );
          if (token !== this.renderToken) return;

          this.grid.innerHTML = '';
          let shown = 0;
          results.forEach((card) => {
            if (!card) return;
            const item = document.createElement('li');
            item.className = 'grid__item';
            item.append(...card.children);
            this.grid.append(item);
            shown += 1;
          });

          if (!shown && this.emptyState) {
            this.emptyState.hidden = false;
          }
        }
      }
    );
  }
})();
